import LockIcon from '@mui/icons-material/Lock';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { IInvoice } from '../../types';

interface InvoiceUpdateFormProps {
  invoice: IInvoice;
  openErrorColor: boolean;
  updateInvoice: (values: any) => void;
}

const InvoiceUpdateForm = ({ invoice, openErrorColor, updateInvoice }: InvoiceUpdateFormProps) => {
  const { t } = useTranslation();

  if (!invoice) return null;

  return (
    <EnhancedForm
      title={'Dettagli'}
      onSubmit={updateInvoice}
      hideControls={false}
      formId="amountForm"
      autoStyleLayout={true}
      columns={3}>
      <IdentityField
        name="ref"
        label={t(`entities.ddt.invoice_ref`)}
        initialValue={invoice.ref}
        component={TextFieldAdapter}
        disabled={invoice.closed}
      />

      <IdentityField
        name="date"
        label={t(`entities.ddt.invoice_date`)}
        component={DatePickerAdapter}
        initialValue={dayjs(invoice.date, 'YYYY-MM-DD').format('YYYY-MM-DD')}
        disabled={invoice.closed}
      />

      <IdentityField
        name="shipping_fare"
        label={t(`entities.ddt.shipping_fare`)}
        initialValue={invoice.shipping_fare}
        component={TextFieldAdapter}
        type="number"
      />

      <div
        className={`${
          openErrorColor && invoice.credit_memo_ref == ''
            ? 'border-2 border-rose-500 text-red-500'
            : ''
        }`}>
        <IdentityField
          name="credit_memo_ref"
          label={t(`entities.ddt.credit_memo_ref`)}
          initialValue={invoice.credit_memo_ref}
          component={TextFieldAdapter}
          type="text"
          disabled={+invoice.credit_memo_amount === 0}
        />
      </div>

      <IdentityField
        name="credit_memo_amount"
        label={t(`entities.ddt.credit_memo_amount`)}
        initialValue={invoice.credit_memo_amount}
        format={(value) => (value != null ? currencyFormatter(value) : '')}
        component={TextFieldAdapter}
        type="text"
        disabled
      />

      <div
        className={`${
          openErrorColor && invoice.credit_memo_ref == ''
            ? 'border-2 border-rose-500 text-red-500'
            : ''
        }`}>
        <IdentityField
          name="credit_memo_date"
          label={t(`entities.ddt.credit_memo_date`)}
          component={DatePickerAdapter}
          initialValue={
            invoice?.credit_memo_date
              ? dayjs(invoice.credit_memo_date, 'YYYY-MM-DD').format('YYYY-MM-DD')
              : null
          }
          disabled={invoice.closed || +invoice.credit_memo_amount === 0}
          disableOpenPicker={invoice.closed || +invoice.credit_memo_amount === 0}
        />
      </div>

      <IdentityField
        name="closed"
        label={invoice?.closed ? t(`entities.ddt.closed`) : t(`entities.ddt.open`)}
        component={CheckboxAdapter}
        type="checkbox"
        initialValue={invoice?.closed ?? false}
        icon={<LockOpenOutlinedIcon />}
        checkedIcon={<LockIcon />}
      />

      <IdentityField
        gridSpan={3}
        name="notes"
        label={t(`global.notes`)}
        component={TextFieldAdapter}
        initialValue={invoice.notes}
      />
    </EnhancedForm>
  );
};

export default InvoiceUpdateForm;
